import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment/moment';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import { Button, styled } from '@mui/material';

import colors from '../../constants/colors';
import { convertGenericHtmlToPdf } from '../../actions/genericHtmlToPdf';

import HeaderFooter from '../../templates/titlesTransaction/HeaderFooter';
import AddressSection from '../../templates/titlesTransaction/AddressSection';
import TitlesSection from '../../templates/titlesTransaction/TitlesSection';
import PropertyDetailsSection from '../../templates/titlesTransaction/PropertyDetailsSection';
import GridLayout from '../../templates/titlesTransaction/GridLayout';
import DisclaimerSection from '../../templates/titlesTransaction/DisclaimerSection';
import generateHtml from '../../templates/htmlGenerator';
import styles from '../../templates/titlesTransaction/styles';
import PDFViewerV2 from '../pdf/PDFViewerV2';
import BlockUi from '../blockUi/BlockUi';
import { getAssetDomainOld, LOGOS } from '../../constants/assets';
import FileHelper from '../../helpers/FileHelper';
import ReportHelper from '../../helpers/report/ReportHelper';
import CLAPI from '../../api/clapi';
import { getTitleDetails } from '../titlesNZ/api/titles';
import { getContentDisclaimer } from '../../actions/clapi';
import Commons from '../../helpers/Commons';
import SEGMENT from '../../constants/segment';
import { trackEvent } from '../../actions/segment';
import SegmentHelper from '../../helpers/Segment';
import { entryPoints } from '../../constants/entryPoint';
import ScrollableContainer from '../common/ScrollableContainer';

const TITLES_TRANSACTION_REPORT = 'Titles_Transaction_Report';
const DOWNLOAD_REPORT = 'Download Report';
const PRINT_PDF_REPORT = 'Print PDF Report';
const NO_INFORMATION_TEXT = 'No Title information is available for this property';
const NOT_FOUND_ERROR = '404 Not Found';
const ERROR_MESSAGE = 'There was an error retrieving the report. Please try again.';

const StyledErrorContainer = styled('div')({
    minHeight: 'calc(100vh - 250px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
});

const StyledButton = styled(Button)({
    width: 119,
    fontSize: 13,
    margin: '0px 4px',
});

const PRIOR_TITLES_HEADER = [
    { label: 'Date Issued', key: 'dateIssued' },
    { label: 'Certificate of Title', key: 'certificateOfTitle' },
    { label: 'Land Area', key: 'landArea' },
    { label: 'Status', key: 'status' },
];

const MEMORIALS_HEADER = [
    { label: 'Date', key: 'date' },
    { label: 'Reference', key: 'reference' },
    { label: 'Document Information', key: 'documentInformation' },
];

const TitlesTransactionModal = ({
    propertyAddress,
    propertyId,
    onClose,
    location,
    route,
    fromActionButton,
}) => {
    const dispatch = useDispatch();

    const [titleNZTokens, setTitleNZTokens] = useState({
        rppToken: null,
        isTokenExists: false
    });
    const [titleDetails, setTitleDetails] = useState();
    const [errorMessage, setErrorMessage] = useState('');
    const [preparing, setPreparing] = useState(true);
    const [copyright, setCopyright] = useState();
    const [copyrightFetching, setCopyrightFetching] = useState(false);


    const contentDisclaimer = useSelector(state => state.clapi.get('contentDisclaimer'));
    const pdfBytes = useSelector(state => state?.genericHtmlToPdf.get('report').pdfBytes);
    const isFetching = useSelector(state => state?.genericHtmlToPdf.get('report').fetching);
    const reportError = useSelector(state => state?.genericHtmlToPdf.get('report').error);
    const titlesDomain = useSelector(state => state?.config.get('applicationDomain').titles);

    const getFilenameWithDate = () => {
        const dateNow = moment().format('DD-MM-YYYY');
        return ReportHelper.getFilename(`${TITLES_TRANSACTION_REPORT}_${propertyAddress}_${dateNow}`);
    };

    const getEntryPointByRoute = useCallback(() => {
        if (route?.id === 'property' && fromActionButton ) {
            return entryPoints.TITLE_INFORMATION;
        } else if (route?.id === 'property' && !fromActionButton) {
            return entryPoints.REPORTS_MENU;
        } else {
            return entryPoints[route?.id].entryPoint;
        }
    }, [route?.id]);

    const handleModalMixpanel = useCallback(() => {
        const properties = {
            propertyId,
            pageContext: route?.pageContext,
            entryPoint: getEntryPointByRoute(),
            reportType: SEGMENT.EVENT_PROPERTIES.TITLES_TRANSACTION_REPORT,
            reportFormat: SEGMENT.EVENT_NAMES.PDF,
        };
        dispatch(trackEvent(SegmentHelper.generateReport(properties)));
    }, [dispatch, getEntryPointByRoute, propertyId, route?.pageContext]);

    const handleMixPanelByEvent = (eventName) => {
        const properties = { propertyId, eventName, entryPoint: getEntryPointByRoute(), reportFormat: 'PDF' };
        dispatch(trackEvent(SegmentHelper.generateTitlesTransactionReport(properties)));
    };

    // onload fetch token and content
    useEffect(() => {
        getTitleNZTokenDetails();
        dispatch(getContentDisclaimer('disclaimers', ['clPublication_nz', 'clData_nz', 'title_nz']));
        setCopyrightFetching(true);
        CLAPI.getLegalContent('copyrights', 'clCopyrightFooter_nz')
            .then(response => {
                setCopyright(response?.content);
            })
            .finally(() => {
                setCopyrightFetching(false);
            });
    }, []);

    // fetch titles details once token is available
    useEffect(() => {
        if (!titleNZTokens.isTokenExists) return;

        const config = {
            configDomain: titlesDomain,
            token: {
                rpp: titleNZTokens.rppToken,
            },
        };

        getTitleDetails({ config, propertyId })
            .then((resp) => {
                const is404Error = resp?.data?.errors?.some(res => res.msg === NOT_FOUND_ERROR);
                const isBadRequest = resp?.status === 400;

                if (resp?.status === 200 && !is404Error) {
                    setTitleDetails(resp.data);
                    handleModalMixpanel();
                } else if (is404Error || isBadRequest) {
                    setErrorMessage(NO_INFORMATION_TEXT);
                } else {
                    setErrorMessage(ERROR_MESSAGE);
                }
            })
            .catch((reason) => {
                setErrorMessage(ERROR_MESSAGE);
            })
            .finally(() => {
                if (!copyrightFetching || contentDisclaimer?.isFetching === false) {
                    setPreparing(false);
                }
            });

    }, [titleNZTokens.isTokenExists, titlesDomain, propertyId]);

    // generate the pdf
    useEffect(() => {
        if (titleDetails &&
            !errorMessage &&
            !copyrightFetching &&
            contentDisclaimer?.isFetching === false) {
                dispatch(convertGenericHtmlToPdf({
                    htmlString: generateHtml(htmlString, styles()),
                }));
                setTimeout(() => {
                    setPreparing(false);
                }, 100);
        }
    }, [titleDetails, errorMessage, contentDisclaimer?.isFetching, copyrightFetching]);

    useEffect(() => {
        if (!isFetching && reportError) {
            setErrorMessage(ERROR_MESSAGE);
        }
    }, [reportError, isFetching]);

    const priorTitleContent = useMemo(() => {
        return titleDetails
            ?.titles?.[0]?.titleDescription?.priorTitleReferences
            ?.map(priorTitle => ({
                dateIssued: '',
                certificateOfTitle: priorTitle,
                landArea: '',
                status: 'Cancelled',
            }));
    }, [titleDetails]);

    const getMappedInterests = useCallback((isCurrent) => {
        return titleDetails?.titles?.[0]?.interests
            ?.filter(interest => interest.isCurrent === isCurrent)
            ?.sort((a, b) => new Date(b.date) - new Date(a.date))
            ?.map(interest => ({
                date: Commons.formatDate(interest.date, 'DD MMM YYYY'),
                reference: interest.instrumentNo,
                documentInformation: interest.description
            }));
    }, [titleDetails]);

    const currentInterests = useMemo(() => getMappedInterests(true), [getMappedInterests]);
    const historicalInterests = useMemo(() => getMappedInterests(false), [getMappedInterests]);
    const referenceNumber = titleDetails?.titles?.[0]?.titleDescription?.reference;


    const htmlString = renderToString(
        <>
            <HeaderFooter
                logo={LOGOS.COTALITY}
                copyright={copyright}
            />
            <div className="body-content">
                <AddressSection
                    streetAddress={location?.locallyFormattedAddress}
                    valuationAddress={location?.singleLine}
                />
                <TitlesSection header="Property Details">
                    <PropertyDetailsSection titleDetails={titleDetails} />
                </TitlesSection>
                {
                    priorTitleContent?.length > 0 &&
                    <TitlesSection header="Prior Titles(s)">
                        <GridLayout
                            headers={PRIOR_TITLES_HEADER}
                            content={priorTitleContent}
                        />
                    </TitlesSection>
                }
                {
                    currentInterests?.length > 0 &&
                    <TitlesSection header={`Current Memorials - ${referenceNumber}`}>
                        <GridLayout
                            headers={MEMORIALS_HEADER}
                            content={currentInterests}
                        />
                    </TitlesSection>
                }
                {
                    historicalInterests?.length > 0 &&
                    <TitlesSection header={`Historical Memorials - ${referenceNumber}`}>
                        <GridLayout
                            headers={MEMORIALS_HEADER}
                            content={historicalInterests}
                        />
                    </TitlesSection>
                }
                <TitlesSection header="Disclaimer" styleClass="page-break-avoid">
                    <DisclaimerSection disclaimer={contentDisclaimer?.disclaimer} />
                </TitlesSection>
            </div>
        </>
    );

    const getTitleNZTokenDetails = () => {
        setPreparing(true);
        CLAPI.getTitleNZTokens()
            .then((resp) => {
                setTitleNZTokens({
                    rppToken: resp.rppToken,
                    isTokenExists: true
                });
            })
            .catch((e) => {
                setErrorMessage(ERROR_MESSAGE);
                setPreparing(false);
            });
    }

    const renderAssetByName = name =>
        <img className="pdf-button-icon" src={getAssetDomainOld(name)} alt={name} width="16px" />;

    const handleDownload = (e) => {
        e.preventDefault();
        FileHelper.downloadPdfFromBase64(pdfBytes, `${getFilenameWithDate()}`);
        handleMixPanelByEvent(DOWNLOAD_REPORT);
    };

    const handlePrint = () => {
        handleMixPanelByEvent(PRINT_PDF_REPORT);
        ReportHelper.printByClassname('.pdf-canvas-wrapper canvas')
    };

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
        >
            <DialogTitle
                sx={{
                    padding: '24px 24px 20px',
                    '& h2': {
                        wordBreak: 'break-all',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: colors.GUN_POWDER,
                    },
                }}
            >
                <span>{propertyAddress}</span>
                <CloseIcon
                    data-testid="title-details-modal-close-icon"
                    className="close-button-mui pull-right"
                    cursor="pointer"
                    onClick={onClose}
                />
            </DialogTitle>
            <DialogContent
                sx={{
                    padding: '0 24px 24px 0',
                    marginRight: 3,
                    overflowY: 'hidden',
                }}
            >
                <div className="pdf-content" style={{ height: 'calc(100vh - 250px)' }}>
                    {
                        errorMessage ?
                            <StyledErrorContainer>{errorMessage}</StyledErrorContainer>
                            :
                            <ScrollableContainer
                                style={{
                                    height: '100%',
                                    overflowX: 'hidden',
                                }}
                            >
                                <BlockUi blocking={preparing || isFetching} renderChildren={false}>
                                    <PDFViewerV2
                                        src={pdfBytes}
                                        fetchingReport={isFetching}
                                        base64
                                        fullPage
                                        pdfScale={1.5}
                                    />
                                </BlockUi>
                            </ScrollableContainer>
                    }
                </div>
            </DialogContent>
            <DialogActions
                sx={{
                    padding: '15px 35px',
                    margin: 0,
                    height: '62px',
                }}
            >
                {
                    !errorMessage &&
                    <>
                        <StyledButton
                            variant="contained"
                            color="secondary"
                            onClick={handlePrint}
                            startIcon={renderAssetByName('print@2x.png')}
                            disabled={!pdfBytes || isFetching || preparing}
                        >
                            Print
                        </StyledButton>
                        <StyledButton
                            variant="contained"
                            color="secondary"
                            onClick={handleDownload}
                            startIcon={renderAssetByName('download@2x.png')}
                            disabled={!pdfBytes || isFetching || preparing}
                        >
                            Download
                        </StyledButton>
                    </>
                }
            </DialogActions>
        </Dialog>
    );
};

TitlesTransactionModal.propTypes = {
    onClose: PropTypes.func,
    propertyAddress: PropTypes.string,
    propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    route: PropTypes.object,
    location: PropTypes.object,
    fromActionButton: PropTypes.bool,
};

export default TitlesTransactionModal;